<template>
    <div
        class="cal-match"
        :class="{
            'cal-match--finished': game.is_finished,
            'cal-match--upcoming': !game.is_finished,
            'cal-match--panel': variant === 'panel',
        }"
        :role="game.is_finished ? undefined : 'button'"
        :tabindex="game.is_finished ? undefined : 0"
        @click="onCardActivate"
        @keydown="onCardKeydown"
    >
        <div class="cal-match__meta">
            <span class="cal-match__time-group">
                <span v-if="game.time" class="cal-match__time">{{ game.time }}</span>
                <span v-else class="cal-match__time cal-match__time--empty" />
                <Link
                    v-if="game.has_stats"
                    :href="statsPath"
                    class="match-stats-icon"
                    title="Статистика матча"
                    @click.stop
                    @keydown.stop
                >
                    <svg width="9" height="9" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                        <path d="M1 14h2V6H1v8zm4 0h2V2H5v12zm4 0h2V8H9v6zm4 0h2V4h-2v10z" fill="currentColor" />
                    </svg>
                </Link>
            </span>
            <component
                :is="resultsPath ? Link : 'div'"
                v-if="competitionIcon || competitionContextLabel || stageMedal"
                :href="resultsPath || undefined"
                class="cal-match__comp"
                :class="{ 'cal-match__comp--link': !!resultsPath }"
                @click.stop
                @keydown.stop
            >
                <img
                    v-if="stageMedal"
                    :src="stageMedal.src"
                    :alt="stageMedal.alt"
                    class="cal-match__stage-medal"
                    width="16"
                    height="16"
                />
                <span v-else-if="competitionContextLabel" class="cal-match__comp-label">{{ competitionContextLabel }}</span>
                <img
                    v-if="competitionIcon"
                    :src="competitionIcon"
                    :alt="competitionAlt"
                    class="cal-match__comp-icon"
                    width="16"
                    height="16"
                />
            </component>
        </div>

        <div class="cal-match__teams">
            <component
                :is="homeTeamPath ? Link : 'div'"
                :href="homeTeamPath || undefined"
                class="cal-match__side cal-match__side--home"
                :class="{
                    'is-winner': game.winner === 'home',
                    'cal-match__side--link': !!homeTeamPath,
                }"
                @click.stop
                @keydown.stop
            >
                <span class="cal-match__logo">
                    <img v-if="game.home_team?.logo_path" :src="game.home_team.logo_path" alt="" />
                    <span v-else>{{ teamInitial(game.home_team) }}</span>
                </span>
                <span class="cal-match__name">{{ game.home_team?.name }}</span>
            </component>

            <div class="cal-match__sep" aria-hidden="true">
                <template v-if="game.is_finished">
                    <span class="cal-match__score">{{ game.home_score }}</span>
                    <span class="cal-match__colon">:</span>
                    <span class="cal-match__score">{{ game.away_score }}</span>
                </template>
                <span v-else class="cal-match__colon">:</span>
            </div>

            <component
                :is="awayTeamPath ? Link : 'div'"
                :href="awayTeamPath || undefined"
                class="cal-match__side cal-match__side--away"
                :class="{
                    'is-winner': game.winner === 'away',
                    'cal-match__side--link': !!awayTeamPath,
                }"
                @click.stop
                @keydown.stop
            >
                <span class="cal-match__logo">
                    <img v-if="game.away_team?.logo_path" :src="game.away_team.logo_path" alt="" />
                    <span v-else>{{ teamInitial(game.away_team) }}</span>
                </span>
                <span class="cal-match__name">{{ game.away_team?.name }}</span>
            </component>
        </div>
    </div>
</template>

<script setup>
import { computed } from 'vue';
import { Link } from '@inertiajs/vue3';
import { COMPETITION_ICONS, COMPETITION_TYPES } from '@/utils/skillQualityConfig';
import { teamPublicPath } from '@/utils/teamUrl';
import { gamePublicPath } from '@/utils/gameUrl';
import { resultsTabPath } from '@/utils/resultsUrl';

const props = defineProps({
    game: {
        type: Object,
        required: true,
    },
    variant: {
        type: String,
        default: 'cell',
        validator: (value) => ['cell', 'panel'].includes(value),
    },
});

const emit = defineEmits(['select']);

const SHORT_STAGE_LABELS = {
    quarterfinal: '1/4',
    semifinal: '1/2',
    '5-places': '5-9',
    dop: 'Доп.',
    playoff: 'Доп.',
};

const STAGE_MEDALS = {
    final: { src: '/images/icons/medal-gold.svg', alt: 'Финал' },
    third_place: { src: '/images/icons/medal-bronze.svg', alt: 'Матч за 3 место' },
};

const REGULAR_STAGES = new Set(['regular', 'regular-1', 'regular-2', '', null, undefined]);

function isRegularStage(stage) {
    return REGULAR_STAGES.has(stage);
}

function shortStageLabel(stage) {
    if (!stage || isRegularStage(stage) || stage === 'group_stage' || STAGE_MEDALS[stage]) {
        return null;
    }
    return SHORT_STAGE_LABELS[stage] || null;
}

const statsPath = computed(() => gamePublicPath(props.game));
const homeTeamPath = computed(() => teamPublicPath(props.game.home_team));
const awayTeamPath = computed(() => teamPublicPath(props.game.away_team));
const resultsPath = computed(() => resultsTabPath(props.game.competition_type));

const competitionIcon = computed(() => {
    const type = props.game.competition_type;
    return type ? COMPETITION_ICONS[type] || null : null;
});

const competitionAlt = computed(() => {
    const type = props.game.competition_type;
    const found = COMPETITION_TYPES.find((item) => item.value === type);
    return found?.label || '';
});

const stageMedal = computed(() => {
    const stage = props.game.stage;
    return STAGE_MEDALS[stage] || null;
});

const competitionContextLabel = computed(() => {
    if (stageMedal.value) {
        return null;
    }

    const type = props.game.competition_type;
    const stage = props.game.stage;
    // DB column is tour_number; formatGame also exposes round_number for BC
    const tour = props.game.tour_number ?? props.game.round_number;
    const hasTour = tour != null && tour !== '';
    const stageShort = shortStageLabel(stage);

    if (type === 'championship') {
        if (hasTour && (isRegularStage(stage) || !stageShort)) {
            return `Тур ${tour}`;
        }
        return stageShort;
    }

    if (type === 'cup') {
        if (stage === 'group_stage' && props.game.cup_group) {
            return `Группа ${props.game.cup_group}`;
        }
        return stageShort;
    }

    return stageShort || props.game.stage_name || null;
});

function teamInitial(team) {
    const name = team?.short_name || team?.name || '?';
    return String(name).charAt(0).toUpperCase();
}

function onCardActivate() {
    if (props.game.is_finished) {
        return;
    }
    emit('select', props.game);
}

function onCardKeydown(event) {
    if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onCardActivate();
    }
}
</script>

<style lang="scss" scoped>
@import '../../../../scss/tokens';

.cal-match {
    display: block;
    width: 100%;
    text-align: left;
    border: 1px solid $color-brand-border;
    border-radius: $radius-sm;
    background: #fff;
    padding: 0.3rem 0.35rem;
    cursor: default;
    transition: border-color 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;

    &:hover {
        border-color: $color-brand;
        box-shadow: 0 1px 4px rgba(39, 80, 146, 0.12);
    }

    &:focus-visible {
        outline: 2px solid $color-brand;
        outline-offset: 1px;
    }

    &--finished {
        background: rgba(255, 255, 255, 0.95);
    }

    &--upcoming {
        cursor: pointer;
        background: $color-brand-alpha-soft;
        border-style: dashed;
    }
}

.cal-match__meta {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.25rem;
    margin-bottom: 0.25rem;
    min-height: 16px;
}

.cal-match__time-group {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    min-width: 0;

    .match-stats-icon {
        width: 16px;
        height: 16px;
        flex-shrink: 0;
    }
}

.cal-match__time {
    font-size: 0.65rem;
    font-weight: 600;
    color: $color-text-secondary;
    line-height: 1;

    &--empty {
        flex: 1;
    }
}

.cal-match__comp {
    display: inline-flex;
    align-items: center;
    gap: 0.2rem;
    flex-shrink: 0;
    margin-left: auto;
    text-decoration: none;
    color: inherit;
    border-radius: 2px;

    &--link {
        cursor: pointer;

        &:hover {
            opacity: 0.85;
        }

        &:focus-visible {
            outline: 2px solid $color-brand;
            outline-offset: 1px;
        }
    }
}

.cal-match__comp-label {
    font-size: 0.6rem;
    font-weight: 600;
    color: $color-text-secondary;
    line-height: 1;
    white-space: nowrap;
}

.cal-match__comp-icon {
    width: 16px;
    height: 16px;
    object-fit: contain;
    flex-shrink: 0;
    display: block;
}

.cal-match__stage-medal {
    width: 16px;
    height: 16px;
    object-fit: contain;
    flex-shrink: 0;
    display: block;
}

.cal-match__teams {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
    align-items: center;
    gap: 0.2rem;
    min-width: 0;
}

.cal-match__side {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    min-width: 0;
    text-decoration: none;
    color: inherit;
    border-radius: 2px;

    &--home {
        justify-content: flex-end;
        text-align: right;
    }

    &--away {
        justify-content: flex-start;
        text-align: left;
    }

    &.is-winner .cal-match__name {
        font-weight: 700;
        color: $color-text-primary;
    }

    &--link {
        cursor: pointer;

        &:hover .cal-match__name {
            color: $color-brand;
        }

        &:focus-visible {
            outline: 2px solid $color-brand;
            outline-offset: 1px;
        }
    }
}

.cal-match__logo {
    width: 16px;
    height: 16px;
    border-radius: 3px;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.5rem;
    font-weight: 700;
    color: $color-brand;
    flex-shrink: 0;

    img {
        width: 100%;
        height: 100%;
        object-fit: contain;
    }
}

.cal-match__name {
    font-size: 0.65rem;
    line-height: 1.15;
    color: $color-text-result-dark;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    min-width: 0;
}

.cal-match__sep {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.12rem;
    flex-shrink: 0;
    padding: 0 0.1rem;
}

.cal-match__score {
    font-size: 0.7rem;
    font-weight: 700;
    color: $color-brand;
    line-height: 1;
    min-width: 0.65rem;
    text-align: center;
}

.cal-match__colon {
    font-size: 0.7rem;
    font-weight: 700;
    color: $color-text-light;
    line-height: 1;
}

.cal-match--panel {
    padding: 0.75rem 0.85rem;

    .cal-match__meta {
        min-height: 20px;
        margin-bottom: 0.45rem;
    }

    .cal-match__time {
        font-size: 0.8rem;
    }

    .cal-match__comp-label {
        font-size: 0.75rem;
    }

    .cal-match__comp-icon,
    .cal-match__stage-medal {
        width: 20px;
        height: 20px;
    }

    .cal-match__teams {
        gap: 0.65rem;
    }

    .cal-match__side {
        gap: 0.5rem;
    }

    .cal-match__logo {
        width: 28px;
        height: 28px;
        font-size: 0.75rem;
        border-radius: 4px;
    }

    .cal-match__name {
        flex: 1;
        font-size: 0.95rem;
        line-height: 1.25;
        white-space: normal;
        display: -webkit-box;
        -webkit-box-orient: vertical;
        -webkit-line-clamp: 2;
        line-clamp: 2;
    }

    .cal-match__score,
    .cal-match__colon {
        font-size: 1rem;
    }
}
</style>
